import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatListModule} from '@angular/material/list';
import {MatLineModule} from '@angular/material/core';
import {CommonModule} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatBottomSheetRef} from '@angular/material/bottom-sheet';
import {MatTooltipModule} from '@angular/material/tooltip';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {Tutorial} from '../api/interfaces/tutorial.interface';
import {TutorialVideoDialogComponent} from './tutorial-video-dialog.component';
import {MatDividerModule} from '@angular/material/divider';
import {PACKAGE_NAMES, TutorialManagement} from '@maporium-workspace/shared';
import {capitalize} from 'lodash';
import {TutorialService} from '../api/services/tutorial.service';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-tutorial-browser',
  styleUrls: ['./tutorial-browser.bottomsheet.component.scss'],
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <span class="icon-smap-concept-help me-2"></span>{{ 'TUTORIALS.HEADING' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" (click)="dismiss()" [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
    </div>
    <div class="d-flex align-items-center justify-content-between mt-2">
      <input type="text"
             [placeholder]="'TUTORIALS.SEARCH_PLACEHOLDER' | translate"
             class="col-8"
             id="tutorial-search-input"
             #searchInput
             autocomplete="off"
             autocapitalize="off"
             autocorrect="off"
             spellcheck="false"
             [formControl]="searchControl">
      <span>{{ getTutorialCount() }}</span>
    </div>
    <mat-divider class="mt-3 mb-1"></mat-divider>
    <mat-nav-list class="list-wrapper">
      <ng-container *ngFor="let tutorial of filteredTutorials; trackBy: trackById">
        <a mat-list-item
           (click)="openTutorial(tutorial); $event.preventDefault(); $event.stopPropagation()"
           class="d-flex justify-content-evenly p-0">
          <span class="row w-100 align-items-center m-0">
            <span class="col-2">
              <button mat-icon-button
                      [matTooltip]="getServicePlanTooltip(tutorial)"
                      matTooltipPosition="above">
                <i [ngClass]="getServicePlanIcon(tutorial)"></i>
              </button>
            </span>
            <span class="col-10 pe-3"
                  [matTooltip]="tutorial.description || ''"
                  matTooltipPosition="below">
              <span matLine>{{ tutorial.name }}</span>
            </span>
          </span>
        </a>

      </ng-container>
    </mat-nav-list>
  `,
  standalone: true,
  imports: [
    MatListModule,
    CommonModule,
    MatLineModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    FormsModule,
    ReactiveFormsModule,
    MatDividerModule,
    TranslatePipe,
  ]
})
export class TutorialBrowserBottomsheetComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly baseTutorials: Tutorial[] = [
    // {
    //   id: '1',
    //   name: "Let's Get Smapping",
    //   description: 'Experience how easy and intuitive it is to create a visual context with SMAP.',
    //   source: 'https://www.youtube.com/embed/p69TuCQDIGM?si=CGovlm6-7ZH3hpWr&rel=0',
    //   minServicePlanId: 'creator'
    // },
    // {
    //   id: '2',
    //   name: 'A Story of Agricultural Automation',
    //   description: 'Experience the diverse perspectives shared in this smap of a complex system.',
    //   source: 'https://www.youtube.com/embed/8kl47ri-PUM?si=1rong1NdPZeCviES&rel=0',
    //   minServicePlanId: 'publisher'
    // }
  ];

  private dynamicTutorials: Tutorial[] = [];
  private allTutorials: Tutorial[] = [];
  public filteredTutorials: Tutorial[] = [];
  readonly packageNames = PACKAGE_NAMES;
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  private subSink: Subscription = new Subscription();
  searchControl = new FormControl('');

  constructor(
    private matDialog: MatDialog,
    private tutorialService: TutorialService,
    private translate: TranslateService,
    private bottomSheetRef: MatBottomSheetRef<TutorialBrowserBottomsheetComponent>
  ) {}

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.searchInputRef?.nativeElement) {
        this.searchInputRef.nativeElement.focus();
      }
    }, 1100);
  }

  ngOnInit() {
    this.loadTutorials();
    this.subSink.add(
      this.searchControl.valueChanges
        .pipe(debounceTime(200), distinctUntilChanged())
        .subscribe(() => this.applyFilters())
    );
  }

  private loadTutorials() {
    // Load dynamic tutorials from backend
    this.subSink.add(
      this.tutorialService.getTutorials().subscribe({
        next: (tutorialSettings) => {
          this.dynamicTutorials = this.convertToTutorialInterface(tutorialSettings?.tutorials || []);
          this.combineAndFilterTutorials();
        },
        error: (error) => {
          console.error('Error loading dynamic tutorials:', error);
          // Fallback to hardcoded tutorials only
          this.dynamicTutorials = [];
          this.combineAndFilterTutorials();
        }
      })
    );
  }

  private convertToTutorialInterface(managementTutorials: TutorialManagement[]): Tutorial[] {
    return managementTutorials
      .filter(tutorial => tutorial.isActive !== false) // Only include active tutorials
      .map(tutorial => ({
        id: tutorial.id,
        name: tutorial.name,
        description: tutorial.description,
        source: tutorial.source,
        minServicePlanId: tutorial.minServicePlanId,
        minServicePlanName: this.getServicePlanName(tutorial.minServicePlanId)
      }));
  }

  private combineAndFilterTutorials() {
    // Combine hardcoded and dynamic tutorials
    this.allTutorials = [...this.baseTutorials, ...this.dynamicTutorials];
    this.applyFilters();
  }

  private getServicePlanName(planId: string): string {
    const planNames: Record<string, string> = {
      'explorer': 'Explorer',
      'creator': 'Creator',
      'publisher': 'Publisher',
      'developer': 'Developer'
    };
    return planNames[planId] || capitalize(planId);
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  applyFilters() {
    const searchText = this.searchControl.value?.toLowerCase() || '';
    this.filteredTutorials = this.allTutorials.filter((tutorial) => {
      return (
        tutorial.name.toLowerCase().includes(searchText) ||
        tutorial.description?.toLowerCase().includes(searchText)
      );
    });
  }

  getTutorialCount(): string {
    const count = this.filteredTutorials.length;
    return this.translate.instant(count === 1 ? 'TUTORIALS.COUNT_SINGULAR' : 'TUTORIALS.COUNT_PLURAL', {count});
  }

  getServicePlanIcon(tutorial: Tutorial): string {
    switch (tutorial.minServicePlanId.toLowerCase()) {
      case 'explorer':
        return 'icon-smap-plan-seeker';
      case 'creator':
        return 'icon-smap-plan-creator';
      case 'publisher':
        return 'icon-smap-plan-publisher';
      case 'developer':
        return 'icon-smap-plan-tester';
      default:
        return 'icon-smap-plan-seeker';
    }
  }

  getServicePlanTooltip(tutorial: Tutorial): string {
    const planName = tutorial.minServicePlanName || tutorial.minServicePlanId;
    return this.translate.instant('TUTORIALS.SERVICE_PLAN_TOOLTIP', {plan: capitalize(planName)});
  }

  openTutorial(tutorial: Tutorial) {
    this.matDialog.open(TutorialVideoDialogComponent, {
      width: '90vw',
      height: '80vh',
      maxWidth: 'none',
      data: { tutorial },
      panelClass: 'tutorial-video-dialog'
    });
  }

  dismiss() {
    this.bottomSheetRef.dismiss();
  }

  trackById(index: number, tutorial: Tutorial): string {
    return tutorial.id;
  }
}
